import { ReactNode } from "react";

import { SparklesIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import {
  Chips,
  DetailField,
  DetailGrid,
  DetailSection,
  EmptyHint,
} from "./detail";

/**
 * Presentational building blocks for rendering an entity's AI `enrichment`
 * object on its show page. The SEO/GEO contract, transliterations and reasoning
 * are identical across cinema verticals; only the feature dimensions differ, so
 * those come in as a label map. All inputs are loosely typed — the show pages
 * feed already-parsed (lenient, `.passthrough()`) view records.
 */

/** Section wrapper titled "AI Enrichment" with an optional confidence badge. */
export function EnrichmentSection({
  confidence,
  action,
  children,
}: {
  confidence?: number | null;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <DetailSection
      title="AI Enrichment"
      icon={<SparklesIcon className="size-4" />}
      action={
        action ??
        (typeof confidence === "number" ? (
          <Badge variant="secondary">
            {Math.round(confidence * 100)}% confidence
          </Badge>
        ) : undefined)
      }
      contentClassName="space-y-6"
    >
      {children}
    </DetailSection>
  );
}

/** Model's free-text rationale for the enrichment. */
export function ReasoningBlock({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Reasoning
      </p>
      <p className="text-sm leading-relaxed text-foreground/90">{text}</p>
    </div>
  );
}

type SeoGeo = {
  normalizedTitle?: string | null;
  seoTitle?: string | null;
  shortDescription?: string | null;
  keywords?: (string | null | undefined)[] | null;
  relatedSearches?: (string | null | undefined)[] | null;
  faq?:
    | (
        | { question?: string | null; answer?: string | null }
        | null
        | undefined
      )[]
    | null;
};

/** SEO/GEO contract: titles, short description, keywords, related searches, FAQ. */
export function SeoGeoBlock({ seo }: { seo?: SeoGeo | null }) {
  if (!seo) return null;
  const faq = (seo.faq ?? []).filter(
    (f): f is { question?: string | null; answer?: string | null } => !!f,
  );
  return (
    <div className="space-y-4">
      <DetailGrid>
        <DetailField label="Normalized title" value={seo.normalizedTitle} />
        <DetailField label="SEO title" value={seo.seoTitle} />
        <DetailField
          label="Short description"
          value={seo.shortDescription}
          full
        />
        <DetailField label="Keywords" full>
          <Chips items={seo.keywords} variant="outline" />
        </DetailField>
        <DetailField label="Related searches" full>
          <Chips items={seo.relatedSearches} variant="outline" />
        </DetailField>
      </DetailGrid>
      {faq.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            FAQ
          </p>
          <ul className="space-y-2">
            {faq.map((item, i) => (
              <li key={i} className="rounded-lg border p-3">
                <p className="text-sm font-medium">{item.question}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {item.answer}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type Transliterations = {
  en?: (string | null | undefined)[] | null;
  el?: (string | null | undefined)[] | null;
  ar?: (string | null | undefined)[] | null;
  ro?: (string | null | undefined)[] | null;
};

const TRANSLITERATION_LANGS: { key: keyof Transliterations; label: string }[] =
  [
    { key: "en", label: "English" },
    { key: "el", label: "Greek" },
    { key: "ar", label: "Arabic" },
    { key: "ro", label: "Romanian" },
  ];

/** Per-language transliteration chip rows. */
export function TransliterationsBlock({
  transliterations,
}: {
  transliterations?: Transliterations | null;
}) {
  if (!transliterations) return null;
  const rows = TRANSLITERATION_LANGS.filter(
    ({ key }) => (transliterations[key] ?? []).length > 0,
  );
  if (!rows.length) return null;
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Transliterations
      </p>
      <DetailGrid>
        {rows.map(({ key, label }) => (
          <DetailField key={key} label={label}>
            <Chips items={transliterations[key]} variant="outline" />
          </DetailField>
        ))}
      </DetailGrid>
    </div>
  );
}

/**
 * Grid of 0–1 feature scores. `labels` maps the backend feature key → display
 * label and also fixes the display order; keys with no numeric value are
 * skipped.
 */
export function FeatureScores({
  features,
  labels,
}: {
  features?: Record<string, unknown> | null;
  labels: Record<string, string>;
}) {
  if (!features) return null;
  const entries = Object.entries(labels)
    .map(([key, label]) => [label, features[key]] as const)
    .filter(([, value]) => typeof value === "number") as [string, number][];

  if (!entries.length) {
    return <EmptyHint>No feature scores.</EmptyHint>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Features
      </p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {entries.map(([label, value]) => (
          <div key={label} className="flex items-center gap-3 text-sm">
            <span className="w-32 shrink-0 truncate text-muted-foreground">
              {label}
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn("h-full rounded-full bg-primary")}
                style={{
                  width: `${Math.round(Math.min(Math.max(value, 0), 1) * 100)}%`,
                }}
              />
            </div>
            <span className="w-8 shrink-0 text-right font-mono text-xs tabular-nums">
              {value.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
